import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import { useI18n, LangSwitch } from "../context/I18nContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, Kanban, Calendar, Users, Receipt, Bell,
  Tooth, CalendarPlus, ChatTeardropDots, CheckCircle, X,
} from "@phosphor-icons/react";

const FEATURES = (t) => [
  { icon: CalendarPlus, key: "feat_book" },
  { icon: Tooth, key: "feat_history" },
  { icon: Receipt, key: "feat_invoices" },
  { icon: Kanban, key: "feat_chart" },
  { icon: Bell, key: "feat_reminders" },
  { icon: ShieldCheck, key: "feat_xray" },
];

export default function Landing() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const fontFamily = lang === "hi" ? "Hind, Manrope, sans-serif" : "Manrope, sans-serif";
  const headingFamily = lang === "hi" ? "Hind, Manrope, sans-serif" : "Manrope, sans-serif";

  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", preferred: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error(lang === "hi" ? "à¤¨à¤¾à¤® à¤”à¤° à¤®à¥‹à¤¬à¤¾à¤‡à¤² à¤¨à¤‚à¤¬à¤° à¤œà¤¼à¤°à¥‚à¤°à¥€ à¤¹à¥ˆà¤‚" : "Name and phone are required");
      return;
    }
    setBusy(true);
    try {
      const messageParts = [];
      if (form.preferred) messageParts.push(`Preferred: ${form.preferred}`);
      if (form.message) messageParts.push(form.message);
      await api.post("/public/enquiries", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        service: form.service || null,
        message: messageParts.join(" Â· ") || null,
        source: "website",
        status: "new",
      });
      // Remember for post-auth pre-fill
      sessionStorage.setItem("pendingBooking", JSON.stringify(form));
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily }}>
      {/* Nav */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <Logo size={44} withText />
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Link to="/login"
              className="hidden sm:inline-flex bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm hover:bg-zinc-100"
              data-testid="nav-login">
              {t("common.signin")}
            </Link>
            <Link to="/register"
              className="inline-flex items-center gap-2 bg-[#E60000] text-white font-semibold rounded-sm px-4 py-2 text-sm hover:bg-[#CC0000]"
              data-testid="nav-register">
              {t("common.signup")} <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero with booking form */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
              {t("landing.tagline")}
            </div>
            <div className="text-2xl sm:text-3xl font-semibold text-zinc-500 mb-3" style={{ fontFamily: headingFamily }}>
              {t("landing.hero_pre")}
            </div>
            <h1
              className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-zinc-900 leading-[0.95]"
              style={{ fontFamily: headingFamily }}>
              <span className="text-[#E60000]">{t("landing.hero_title").split(".")[0]}</span>
              {t("landing.hero_title").includes(".") ? "." : ""}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-600 max-w-xl leading-relaxed">
              {t("landing.hero_body")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#book" className="inline-flex items-center gap-2 bg-[#E60000] text-white font-semibold rounded-sm px-6 py-3 text-sm hover:bg-[#CC0000]" data-testid="hero-book-cta">
                {t("landing.cta_book")} <ArrowRight size={16} weight="bold" />
              </a>
              <Link to="/login"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white font-semibold rounded-sm px-6 py-3 text-sm hover:bg-zinc-800"
                data-testid="hero-cta-signin">
                {t("landing.cta_signin")}
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-6">
              <FactRow icon={Calendar} label={lang==="hi" ? "à¤‘à¤¨à¤²à¤¾à¤‡à¤¨ à¤¬à¥à¤•à¤¿à¤‚à¤—" : "Online booking"}/>
              <FactRow icon={ChatTeardropDots} label={lang==="hi" ? "SMS + WhatsApp" : "SMS + WhatsApp"}/>
              <FactRow icon={ShieldCheck} label={lang==="hi" ? "à¤¸à¥à¤°à¤•à¥à¤·à¤¿à¤¤ à¤°à¤¿à¤•à¥‰à¤°à¥à¤¡" : "Secure records"}/>
            </div>
          </div>

          {/* Booking form */}
          <div id="book" className="lg:col-span-5">
            <div className="bg-[#0A0A0A] text-white rounded-sm p-6 sm:p-8 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#E60000] rounded-full opacity-30 blur-3xl"/>
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">
                  {t("landing.tagline")}
                </div>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tighter mb-1" style={{ fontFamily: headingFamily }}>
                  {t("landing.section_book_title")}
                </h2>
                <p className="text-sm text-zinc-400 mb-6">{t("landing.section_book_subtitle")}</p>

                <form onSubmit={submit} className="space-y-3" data-testid="landing-booking-form">
                  <DarkField label={t("landing.field_name")}>
                    <input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className={darkInp} data-testid="lb-name"/>
                  </DarkField>
                  <div className="grid grid-cols-2 gap-3">
                    <DarkField label={t("landing.field_phone")}>
                      <input required value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className={darkInp} data-testid="lb-phone"/>
                    </DarkField>
                    <DarkField label={t("landing.field_email")}>
                      <input type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className={darkInp} data-testid="lb-email"/>
                    </DarkField>
                  </div>
                  <DarkField label={t("landing.field_service")}>
                    <input value={form.service} onChange={(e)=>setForm({...form,service:e.target.value})} className={darkInp}
                      placeholder={t("landing.placeholder_service")} data-testid="lb-service"/>
                  </DarkField>
                  <DarkField label={t("landing.field_preferred")}>
                    <input type="date" value={form.preferred} onChange={(e)=>setForm({...form,preferred:e.target.value})} className={darkInp} data-testid="lb-date"/>
                  </DarkField>
                  <DarkField label={t("landing.field_message")}>
                    <textarea rows={2} value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} className={darkInp} data-testid="lb-message"/>
                  </DarkField>
                  <button type="submit" disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#CC0000] disabled:opacity-60 text-white font-semibold rounded-sm px-6 py-3 text-sm"
                    data-testid="lb-submit">
                    {busy ? "â€¦" : t("landing.submit")} <ArrowRight size={14} weight="bold"/>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-zinc-200 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
          <div className="max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-zinc-900" style={{ fontFamily: headingFamily }}>
              {t("landing.features_title")}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES(t).map((f, i) => (
              <div key={i} className="bg-white border border-zinc-200 rounded-sm p-6 hover:border-zinc-900 transition-colors">
                <f.icon size={32} weight="duotone" color="#E60000" />
                <h3 className="mt-4 text-lg font-bold text-zinc-900" style={{ fontFamily: headingFamily }}>
                  {t(`landing.${f.key}`)}
                </h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{t(`landing.${f.key}_body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-500">
          <Logo size={32} withText />
          <span>Â© Dr Tayyeb&apos;s Dental &amp; Maxillofacial Centre Â· Unchwa, Gorakhpur</span>
        </div>
      </footer>

      {submitted && (
        <SuccessModal onClose={()=>setSubmitted(false)} onSignin={()=>navigate("/login")} onSignup={()=>navigate("/register")}/>
      )}
    </div>
  );
}

const darkInp = "w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#E60000] placeholder-zinc-500";

function DarkField({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function FactRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-xs text-zinc-700">
      <Icon size={16} weight="bold" color="#E60000"/> <span className="font-semibold">{label}</span>
    </div>
  );
}

function SuccessModal({ onClose, onSignin, onSignup }) {
  const { t } = useI18n();
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-sm max-w-md w-full" data-testid="landing-success-modal">
        <div className="px-6 py-5 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle size={22} weight="duotone" color="#16A34A"/>
            <h3 className="font-bold tracking-tight" style={{fontFamily:"Manrope, sans-serif"}}>{t("landing.success_title")}</h3>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-6">
          <p className="text-sm text-zinc-700">{t("landing.success_body")}</p>
          <div className="mt-5 border-t border-zinc-200 pt-5">
            <p className="text-sm font-semibold text-zinc-900">{t("landing.success_track")}</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <button onClick={onSignin} className="flex-1 bg-zinc-900 text-white font-semibold rounded-sm px-4 py-2.5 text-sm hover:bg-zinc-800" data-testid="success-signin">
                {t("landing.signin_now")}
              </button>
              <button onClick={onSignup} className="flex-1 bg-[#E60000] text-white font-semibold rounded-sm px-4 py-2.5 text-sm hover:bg-[#CC0000]" data-testid="success-signup">
                {t("landing.create_now")}
              </button>
            </div>
            <button onClick={onClose} className="mt-3 text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 hover:text-zinc-900 w-full text-center" data-testid="success-continue">
              {t("landing.continue")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
